import { useEffect, useRef, useState } from "react";

// The HTML `capture` attribute on a file input is a mobile-only hint — real
// phone browsers use it to jump straight into the native camera app. Desktop
// browsers have no such concept and just show the plain file picker/explorer
// regardless of `capture`, which is exactly the bug this fixes: on a laptop
// there's no native "camera app" for the browser to hand off to, so the only
// way to get an actual live camera capture is to open one ourselves via
// getUserMedia. Mobile keeps using the native `capture` input unchanged,
// since that's already working well there.
function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPod/i.test(ua)) return true;
  // iPadOS reports its UA as a Mac — the touch-point check catches it.
  if (/Mac/i.test(navigator.platform || "") && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Drop-in replacement for a `<label><input type="file" capture> ... </label>`
 * "Take Photo" control. `onCapture(file)` receives a File either way —
 * from the native camera app on mobile, or from an in-page getUserMedia
 * capture on desktop.
 */
export default function CameraCaptureButton({ label = "📷 Take Photo", busyLabel = "Uploading...", busy = false, onCapture, className = "location-btn", style }) {
  const [mobile] = useState(isMobileDevice);
  const [modalOpen, setModalOpen] = useState(false);
  const [streamError, setStreamError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (!modalOpen) return;
    let cancelled = false;
    setStreamError("");
    navigator.mediaDevices?.getUserMedia?.({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch((err) => {
        if (!cancelled) setStreamError(err?.name === "NotAllowedError"
          ? "Camera access was denied. Use \"Choose from Device\" instead, or allow camera access and try again."
          : "Could not access your camera. Use \"Choose from Device\" instead.");
      });
    return () => { cancelled = true; stopStream(); };
  }, [modalOpen]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture(file);
      setModalOpen(false);
    }, "image/jpeg", 0.9);
  };

  if (mobile) {
    return (
      <label className={className} style={{ display: "inline-flex", justifyContent: "center", cursor: "pointer", flex: 1, ...style }}>
        {busy ? busyLabel : label}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onCapture(file);
            e.target.value = "";
          }}
        />
      </label>
    );
  }

  return (
    <>
      <button
        type="button"
        className={className}
        style={{ display: "inline-flex", justifyContent: "center", flex: 1, ...style }}
        disabled={busy}
        onClick={() => setModalOpen(true)}
      >
        {busy ? busyLabel : label}
      </button>

      {modalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
          <div className="delivery-card" style={{ maxWidth: "480px", width: "100%", margin: 0 }}>
            <h3 style={{ marginBottom: "12px" }}>Camera</h3>
            {streamError ? (
              <p className="error">{streamError}</p>
            ) : (
              <video ref={videoRef} playsInline muted style={{ width: "100%", borderRadius: "8px", background: "#000" }} />
            )}
            <div className="row" style={{ marginTop: "12px" }}>
              <button type="button" className="secondary" onClick={() => setModalOpen(false)}>Cancel</button>
              {!streamError && <button type="button" onClick={handleCapture}>📸 Capture</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
